import "@dxos/node-std/globals"

// packages/core/echo/echo-db/src/packlets/api/result-set.ts
import assert from "@dxos/node-std/assert";
import { Event } from "@dxos/async";
var ResultSet = class {
  constructor(itemUpdate, getter) {
    this._resultsUpdate = new Event();
    /**
    * Triggered when `value` updates.
    */
    this.update = this._resultsUpdate;
    assert(itemUpdate);
    assert(getter);
    this._itemUpdate = itemUpdate;
    this._getter = getter;
    this._results = this._getter();
    this._resultsUpdate.addEffect(() => this._itemUpdate.on(() => {
      this._results = this._getter();
      this._resultsUpdate.emit(this._results);
    }));
  }
  get value() {
    return this._results;
  }
  get first() {
    const value = this._results;
    assert(value.length);
    return value[0];
  }
  /**
  * Subscribe for updates.
  * @param listener
  */
  subscribe(listener) {
    return this._resultsUpdate.on(listener);
  }
  /**
  * Waits for condition to be true and then returns the value that passed the condition first.
  *
  * Current value is also checked.
  */
  waitFor(condition) {
    if (condition(this.value)) {
      return Promise.resolve(this.value);
    }
    return this._resultsUpdate.waitFor(condition);
  }
};

// packages/core/echo/echo-db/src/packlets/api/schema.ts
var TYPE_SCHEMA = "dxos:type/schema";
var Schema = class {
  // prettier-ignore
  constructor(_schema) {
    this._schema = _schema;
  }
  get name() {
    return this._schema.get("schema");
  }
  get fields() {
    var _a;
    return Object.values((_a = this._schema.get("fields")) != null ? _a : {});
  }
  getField(key) {
    return this.fields.find((field) => field.key === key);
  }
  // TODO(kaplanski): What happens if an item has extra properties?
  validate(model) {
    return this.fields.every((field) => {
      const value = model.get(field.key);
      if (!value) {
        return !field.required;
      }
      if (field.type) {
        if (typeof value !== field.type) {
          return false;
        }
      }
      if (field.ref) {
      }
      return true;
    });
  }
  // TODO(kaplanski): Should the field be added to each item using the schema in the space? (Empty value?)
  // TODO(kaplanski): Should the type be infered from the first value added?
  async addField(newField) {
    const newFields = [
      ...this.fields,
      newField
    ];
    await this._schema.set("fields", newFields);
  }
  // TODO(kaplanski): Should editing a field modify all existing items using this schema?
  async editField(currentKey, editedField) {
    const newFields = this.fields.map((field) => {
      if (field.key === currentKey) {
        return editedField;
      }
      return field;
    });
    await this._schema.set("fields", newFields);
  }
  async deleteField(key) {
    const newFields = this.fields.filter((field) => field.key !== key);
    await this._schema.set("fields", newFields);
  }
};

// packages/core/echo/echo-db/src/packlets/api/subscription.ts
import { Event as Event2 } from "@dxos/async";
import { Stream } from "@dxos/codec-protobuf";
var resultSetToStream = (resultSet, map) => new Stream(({ next }) => {
  next(map(resultSet.value));
  return resultSet.update.on(() => next(map(resultSet.value)));
});
var streamToResultSet = (stream, map) => {
  const event = new Event2();
  let lastItem;
  stream.subscribe((data) => {
    lastItem = data;
    event.emit();
  });
  return new ResultSet(event, () => map(lastItem));
};

// packages/core/echo/echo-db/src/packlets/database/builder.ts
var tagMutationsInBatch = (batch, tag) => {
  var _a;
  (_a = batch.objects) == null ? void 0 : _a.forEach((object, objectIndex) => {
    var _a2, _b;
    (_a2 = object.meta) != null ? _a2 : object.meta = {};
    object.meta.clientTag = `${tag}:${objectIndex}`;
    (_b = object.mutations) == null ? void 0 : _b.forEach((mutation, mutationIndex) => {
      var _a3;
      (_a3 = mutation.meta) != null ? _a3 : mutation.meta = {};
      mutation.meta.clientTag = `${tag}:${objectIndex}:${mutationIndex}`;
    });
  });
};
var setMetadataOnObject = (object, meta) => {
  var _a;
  object.meta = meta;
  (_a = object.mutations) == null ? void 0 : _a.forEach((mutation) => {
    mutation.meta = meta;
  });
};
var createModelMutation = (objectId, mutation) => ({
  objects: [
    {
      objectId,
      mutations: [
        {
          model: mutation
        }
      ]
    }
  ]
});
var encodeModelMutation = (meta, mutation) => meta.mutationCodec.encodeAsAny(mutation);
var genesisMutation = (objectId, modelType) => ({
  objects: [
    {
      objectId,
      genesis: {
        modelType
      }
    }
  ]
});

// packages/core/echo/echo-db/src/packlets/database/database-proxy.ts
import assert3 from "@dxos/node-std/assert";
import { asyncTimeout, Event as Event3, Trigger } from "@dxos/async";
import { Context } from "@dxos/context";
import { PublicKey } from "@dxos/keys";
import { log } from "@dxos/log";

// packages/core/echo/echo-db/src/packlets/database/batch.ts
import assert2 from "@dxos/node-std/assert";
var Batch = class {
  constructor() {
    this.data = {
      objects: []
    };
    this.clientTag = null;
    this.receiptTrigger = null;
    this.processTrigger = null;
    this.receipt = null;
    this.processed = false;
  }
  get committing() {
    return !!this.receiptTrigger;
  }
  getReceipt() {
    assert2(this.receiptTrigger);
    return this.receiptTrigger.wait();
  }
  waitToBeProcessed() {
    assert2(this.processTrigger);
    return this.processTrigger.wait();
  }
};

// packages/core/echo/echo-db/src/packlets/database/database-proxy.ts
var FLUSH_TIMEOUT = 5e3;
var DatabaseProxy = class {
  // prettier-ignore
  constructor(_service, _spaceKey) {
    this._service = _service;
    this._spaceKey = _spaceKey;
    this._ctx = new Context();
    this.itemUpdate = new Event3();
    this._clientTagPrefix = PublicKey.random().toHex().slice(0, 8);
    this._clientTagCounter = 0;
    this._pendingBatches = /* @__PURE__ */ new Map();
    this._opening = false;
  }
  get isReadOnly() {
    return false;
  }
  get currentBatch() {
    return this._currentBatch;
  }
  async open(itemManager, modelFactory) {
    assert3(!this._opening);
    this._opening = true;
    this._itemManager = itemManager;
    this._itemManager._debugLabel = "proxy";
    modelFactory.registered.on(this._ctx, async (model) => {
      for (const item of this._itemManager.getUninitializedEntities()) {
        if (item.modelType === model.meta.type) {
          await this._itemManager.initializeModel(item.id);
        }
      }
    });
    const loaded = new Trigger();
    assert3(!this._entities);
    this._entities = this._service.subscribe({
      spaceKey: this._spaceKey
    });
    this._entities.subscribe(async (msg) => {
      var _a, _b;
      log("process", {
        clientTag: msg.clientTag,
        feedKey: msg.feedKey,
        seq: msg.seq,
        objectCount: (_b = (_a = msg.batch.objects) == null ? void 0 : _a.length) != null ? _b : 0
      }, {
        file: "database-proxy.ts",
        line: 90,
        scope: this,
        callSite: (f, a) => f(...a)
      });
      const objectsUpdated = [];
      this._processMessage(msg.batch, objectsUpdated);
      if (msg.clientTag) {
        const batch = this._pendingBatches.get(msg.clientTag);
        if (batch) {
          assert3(msg.feedKey !== void 0);
          assert3(msg.seq !== void 0);
          batch.receipt = {
            feedKey: msg.feedKey,
            seq: msg.seq
          };
          batch.receiptTrigger.wake(batch.receipt);
          batch.processTrigger.wake();
        } else {
        }
      }
      loaded.wake();
      this.itemUpdate.emit(objectsUpdated);
    }, (err) => {
      if (err) {
        log.warn("Connection closed", err, {
          file: "database-proxy.ts",
          line: 126,
          scope: this,
          callSite: (f, a) => f(...a)
        });
      }
    });
    await loaded.wait();
  }
  _processMessage(batch, objectsUpdated = []) {
    var _a;
    for (const object of (_a = batch.objects) != null ? _a : []) {
      assert3(object.objectId);
      let entity;
      if (object.genesis && !this._itemManager.entities.has(object.objectId)) {
        log("construct", {
          object
        }, {
          file: "database-proxy.ts",
          line: 141,
          scope: this,
          callSite: (f, a) => f(...a)
        });
        assert3(object.genesis.modelType);
        entity = this._itemManager.constructItem({
          itemId: object.objectId,
          modelType: object.genesis.modelType
        });
      } else {
        entity = this._itemManager.entities.get(object.objectId);
      }
      if (!entity) {
        log.warn("Item not found", {
          objectId: object.objectId
        }, {
          file: "database-proxy.ts",
          line: 151,
          scope: this,
          callSite: (f, a) => f(...a)
        });
        return;
      }
      objectsUpdated.push(entity);
      if (object.snapshot) {
        log("reset to snapshot", {
          object
        }, {
          file: "database-proxy.ts",
          line: 157,
          scope: this,
          callSite: (f, a) => f(...a)
        });
        entity.resetToSnapshot(object);
      } else if (object.mutations) {
        for (const mutation of object.mutations) {
          log("mutate", {
            id: object.objectId,
            mutation
          }, {
            file: "database-proxy.ts",
            line: 161,
            scope: this,
            callSite: (f, a) => f(...a)
          });
          entity.processMessage(mutation);
        }
      }
    }
  }
  _processOptimistic(objectMutation) {
    assert3(objectMutation.objectId);
    let entity;
    if (objectMutation.genesis && !this._itemManager.entities.has(objectMutation.objectId)) {
      log("construct optimistic", {
        object: objectMutation
      }, {
        file: "database-proxy.ts",
        line: 173,
        scope: this,
        callSite: (f, a) => f(...a)
      });
      assert3(objectMutation.genesis.modelType);
      entity = this._itemManager.constructItem({
        itemId: objectMutation.objectId,
        modelType: objectMutation.genesis.modelType
      });
    } else {
      entity = this._itemManager.entities.get(objectMutation.objectId);
    }
    if (!entity) {
      log.warn("Item not found", {
        objectId: objectMutation.objectId
      }, {
        file: "database-proxy.ts",
        line: 183,
        scope: this,
        callSite: (f, a) => f(...a)
      });
      return;
    }
    if (objectMutation.snapshot) {
      log("reset to snapshot", {
        object: objectMutation
      }, {
        file: "database-proxy.ts",
        line: 188,
        scope: this,
        callSite: (f, a) => f(...a)
      });
      entity.resetToSnapshot(objectMutation);
    } else if (objectMutation.mutations) {
      for (const mutation of objectMutation.mutations) {
        log("mutate", {
          id: objectMutation.objectId,
          mutation
        }, {
          file: "database-proxy.ts",
          line: 192,
          scope: this,
          callSite: (f, a) => f(...a)
        });
        entity.processOptimisticMutation(mutation);
      }
    }
    return entity;
  }
  /**
  * Begins a batch of mutations. If a batch is already in progress, this method does nothing.
  * Batches apply mutations to the database atomically. U
  * @returns true if a batch was started, false if there was already a batch in progress.
  */
  beginBatch() {
    if (this._currentBatch) {
      return false;
    }
    this._currentBatch = new Batch();
    this._currentBatch.clientTag = `${this._clientTagPrefix}:${this._clientTagCounter++}`;
    return true;
  }
  commitBatch() {
    const batch = this._currentBatch;
    assert3(batch);
    this._currentBatch = void 0;
    assert3(!batch.committing);
    assert3(!batch.processTrigger);
    assert3(batch.clientTag);
    assert3(!this._pendingBatches.has(batch.clientTag));
    batch.processTrigger = new Trigger();
    batch.receiptTrigger = new Trigger();
    this._pendingBatches.set(batch.clientTag, batch);
    this._service.write({
      batch: batch.data,
      spaceKey: this._spaceKey,
      clientTag: batch.clientTag
    }).then((receipt) => {
      log("commit", {
        clientTag: batch.clientTag,
        feedKey: receipt.feedKey,
        seq: receipt.seq
      }, {
        file: "database-proxy.ts",
        line: 236,
        scope: this,
        callSite: (f, a) => f(...a)
      });
    }, (err) => {
      batch.receiptTrigger.throw(err);
    });
  }
  // TODO(dmaretskyi): Revert batch.
  mutate(batchInput) {
    var _a, _b, _c, _d;
    assert3(this._itemManager, "Not open");
    if (this._ctx.disposed) {
      throw new Error("Database is closed");
    }
    const batchCreated = this.beginBatch();
    try {
      this._currentBatch.data.objects.push(...(_a = batchInput.objects) != null ? _a : []);
      const objectsUpdated = [];
      tagMutationsInBatch(batchInput, this._currentBatch.clientTag);
      log("mutate", {
        clientTag: this._currentBatch.clientTag,
        objectCount: (_c = (_b = batchInput.objects) == null ? void 0 : _b.length) != null ? _c : 0
      }, {
        file: "database-proxy.ts",
        line: 260,
        scope: this,
        callSite: (f, a) => f(...a)
      });
      for (const objectMutation of (_d = batchInput.objects) != null ? _d : []) {
        const item = this._processOptimistic(objectMutation);
        if (item) {
          objectsUpdated.push(item);
        }
      }
      this.itemUpdate.emit(objectsUpdated);
      const batch = this._currentBatch;
      return {
        objectsUpdated,
        batch
      };
    } finally {
      if (batchCreated) {
        this.commitBatch();
      }
    }
  }
  async flush({ timeout } = {}) {
    const promise = Promise.all(Array.from(this._pendingBatches.values()).map((batch) => batch.waitToBeProcessed()));
    if (timeout) {
      await asyncTimeout(promise, timeout);
    } else {
      await promise;
    }
  }
  async close() {
    var _a;
    await this._ctx.dispose();
    try {
      await this.flush({
        timeout: FLUSH_TIMEOUT
      });
    } catch (err) {
      log.error("timeout waiting for mutations to flush", {
        timeout: FLUSH_TIMEOUT,
        mutationTags: Array.from(this._pendingBatches.keys())
      }, {
        file: "database-proxy.ts",
        line: 299,
        scope: this,
        callSite: (f, a) => f(...a)
      });
    }
    await ((_a = this._entities) == null ? void 0 : _a.close());
    this._entities = void 0;
  }
};

// packages/core/echo/echo-db/src/packlets/database/item.ts
import assert5 from "@dxos/node-std/assert";
import { Event as Event4, scheduleTask } from "@dxos/async";
import { Context as Context2 } from "@dxos/context";
import { log as log2, logInfo } from "@dxos/log";
import { EchoObject } from "@dxos/protocols/proto/dxos/echo/object";

// packages/core/echo/echo-db/src/packlets/database/ordering.ts
import assert4 from "@dxos/node-std/assert";
import { PublicKey as PublicKey2 } from "@dxos/keys";
import { Timeframe } from "@dxos/timeframe";
var getInsertionIndex = (existing, newEntry) => {
  let start = existing.length - 1;
  for (const ourKey = PublicKey2.from(newEntry.mutation.meta.feedKey); start >= 0; start--) {
    if (ourKey.equals(existing[start].mutation.meta.feedKey)) {
      break;
    }
  }
  for (let i = start + 1; i < existing.length; i++) {
    const existingTimeframe = Timeframe.merge(existing[i].mutation.meta.timeframe, new Timeframe([
      [
        PublicKey2.from(existing[i].mutation.meta.feedKey),
        existing[i].mutation.meta.seq - 1
      ]
    ]));
    const deps = Timeframe.dependencies(newEntry.mutation.meta.timeframe, existingTimeframe);
    if (deps.isEmpty()) {
      if (PublicKey2.from(newEntry.mutation.meta.feedKey).toHex() < PublicKey2.from(existing[i].mutation.meta.feedKey).toHex()) {
        return i;
      }
    }
  }
  return existing.length;
};
var MutationQueue = class {
  constructor() {
    this._confirmed = [];
    this._optimistic = [];
  }
  getConfirmedMutations() {
    return this._confirmed;
  }
  getMutations() {
    return [
      ...this._confirmed,
      ...this._optimistic
    ];
  }
  /**
  * Pushes optimistic mutation.
  * The mutation is always appended to the end of the queue.
  */
  pushOptimistic(entry) {
    assert4(entry.mutation.meta.clientTag);
    this._optimistic.push(entry);
  }
  /**
  * Pushes confirmed mutation (coming for the pipeline).
  * Mutation must have feed metadata and timeframe dependencies.
  * Pops optimistic mutation with the same tag.
  */
  pushConfirmed(entry) {
    assert4(entry.mutation.meta.feedKey);
    assert4(typeof entry.mutation.meta.seq === "number");
    assert4(entry.mutation.meta.timeframe);
    const optimisticIndex = !entry.mutation.meta.clientTag ? -1 : this._optimistic.findIndex((message) => message.mutation.meta.clientTag && message.mutation.meta.clientTag === entry.mutation.meta.clientTag);
    if (optimisticIndex !== -1) {
      this._optimistic.splice(optimisticIndex, 1);
    }
    const insertionIndex = getInsertionIndex(this._confirmed, entry);
    const lengthBefore = this._confirmed.length;
    this._confirmed.splice(insertionIndex, 0, entry);
    return {
      reorder: insertionIndex !== lengthBefore || optimisticIndex > 0 || optimisticIndex === -1 && this._optimistic.length > 0,
      apply: optimisticIndex === -1
    };
  }
  resetConfirmed() {
    return this._confirmed = [];
  }
};

// packages/core/echo/echo-db/src/packlets/database/item.ts
var __decorate = function(decorators, target, key, desc) {
  var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function")
    r = Reflect.decorate(decorators, target, key, desc);
  else
    for (var i = decorators.length - 1; i >= 0; i--)
      if (d = decorators[i])
        r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var Item = class {
  /**
  * Items are constructed by the `Database` object.
  * @param itemManager
  * @param objectId        Addressable ID.
  * @param parent        Parent Item (if not a root Item).
  */
  constructor(_itemManager, _id) {
    this._itemManager = _itemManager;
    this._id = _id;
    this._pendingWrites = /* @__PURE__ */ new Set();
    this._mutationProcessed = new Event4();
    this._modelMeta = null;
    this._initialStateMutations = [];
    this._mutationQueue = new MutationQueue();
    this._parent = null;
    this._deleted = false;
    this._stateMachine = null;
    this._initialState = {
      objectId: _id
    };
  }
  get id() {
    return this._id;
  }
  get modelMeta() {
    var _a;
    return (_a = this._modelMeta) != null ? _a : void 0;
  }
  get modelType() {
    assert5(this._modelMeta);
    return this._modelMeta.type;
  }
  get initialized() {
    return !!this._modelMeta;
  }
  get deleted() {
    return this._deleted;
  }
  get parent() {
    return this._parent;
  }
  get state() {
    assert5(this._stateMachine);
    return this._stateMachine.getState();
  }
  toString() {
    var _a;
    return `Item(${JSON.stringify({
      objectId: this.id,
      parentId: this.parent,
      deleted: this.deleted,
      type: (_a = this.modelMeta) == null ? void 0 : _a.type
    })})`;
  }
  /**
  * Perform late intitalization.
  *
  * Only possible if the modelContructor wasn't passed during StateManager's creation.
  */
  initialize(modelConstructor) {
    assert5(!this._modelMeta, "Already iniitalized.");
    this._modelMeta = modelConstructor.meta;
    log2("initialize", {}, {
      file: "item.ts",
      line: 141,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    this._resetState();
  }
  /**
  * @internal
  * Waits for pending operations to complete.
  */
  async destroy() {
    log2("destroy", {}, {
      file: "item.ts",
      line: 151,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    try {
      await Promise.all(this._pendingWrites);
    } catch (e) {
    }
  }
  /**
  * Apply mutation.
  * Processes both system metadata & model-specific updates.
  */
  _applyMutation(entry) {
    log2("_processMutation", {
      entry
    }, {
      file: "item.ts",
      line: 162,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    const { action, parentId, model } = entry.mutation;
    {
      switch (action) {
        case EchoObject.Mutation.Action.DELETE: {
          this._deleted = true;
          break;
        }
        case EchoObject.Mutation.Action.RESTORE: {
          this._deleted = false;
          break;
        }
      }
      if (parentId) {
        this._parent = parentId;
      }
    }
    assert5(!!model === !!entry.decodedModelMutation);
    if (model && this.initialized) {
      this._stateMachine.process(entry.decodedModelMutation);
    }
  }
  _decodeMutation(mutation) {
    assert5(this.modelMeta);
    return {
      mutation,
      decodedModelMutation: !mutation.model ? void 0 : this.modelMeta.mutationCodec.decode(mutation.model.value)
    };
  }
  /**
  * Re-creates the state machine based on the current snapshot and enqueued mutations.
  */
  _resetState() {
    var _a, _b, _c, _d;
    assert5(this._modelMeta, "Model not initialized.");
    log2("Reset state machine", {}, {
      file: "item.ts",
      line: 203,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    this._parent = (_b = (_a = this._initialState.snapshot) == null ? void 0 : _a.parentId) != null ? _b : null;
    this._deleted = (_d = (_c = this._initialState.snapshot) == null ? void 0 : _c.deleted) != null ? _d : false;
    if (this._initialState.snapshot) {
      if (!this._stateMachine) {
        this._stateMachine = this._modelMeta.stateMachine();
      }
      assert5(this._modelMeta.snapshotCodec);
      const decoded = this._modelMeta.snapshotCodec.decode(this._initialState.snapshot.model.value);
      this._stateMachine.reset(decoded);
    } else {
      this._stateMachine = this._modelMeta.stateMachine();
    }
    for (const mutation of this._initialStateMutations) {
      this._applyMutation(mutation);
    }
    for (const mutation1 of this._mutationQueue.getMutations()) {
      this._applyMutation(mutation1);
    }
  }
  processOptimisticMutation(mutation) {
    log2("process optimistic mutation", {
      mutation
    }, {
      file: "item.ts",
      line: 232,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    const queueEntry = this._decodeMutation(mutation);
    this._mutationQueue.pushOptimistic(queueEntry);
    if (this.initialized) {
      log2("Optimistic apply", mutation, {
        file: "item.ts",
        line: 239,
        scope: this,
        callSite: (f, a) => f(...a)
      });
      this._applyMutation(queueEntry);
    }
  }
  /**
  * Processes mutations from the inbound stream.
  */
  processMessage(mutation) {
    const queueEntry = this._decodeMutation(mutation);
    const { reorder, apply } = this._mutationQueue.pushConfirmed(queueEntry);
    log2("process message", {
      mutation: queueEntry,
      reorder,
      apply
    }, {
      file: "item.ts",
      line: 251,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    if (this.initialized) {
      if (reorder) {
        log2("Reset due to order change", {}, {
          file: "item.ts",
          line: 262,
          scope: this,
          callSite: (f, a) => f(...a)
        });
        this._resetState();
      } else if (apply) {
        log2("Apply", {
          meta: queueEntry.mutation.meta
        }, {
          file: "item.ts",
          line: 265,
          scope: this,
          callSite: (f, a) => f(...a)
        });
        this._applyMutation(queueEntry);
      }
    }
    log2("test confirmed state", this.state, {
      file: "item.ts",
      line: 271,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    scheduleTask(new Context2(), () => this._mutationProcessed.emit(queueEntry.mutation.meta));
  }
  /**
  * Create a snapshot of the current state.
  */
  createSnapshot() {
    var _a, _b, _c;
    const commonSnapshot = {
      objectId: this._id,
      genesis: {
        modelType: this.modelType
      },
      snapshot: {
        ...this._initialState.snapshot,
        parentId: (_a = this.parent) != null ? _a : void 0,
        deleted: this.deleted
      }
    };
    if (this.initialized && this.modelMeta.snapshotCodec && typeof ((_b = this._stateMachine) == null ? void 0 : _b.snapshot) === "function") {
      return {
        ...commonSnapshot,
        snapshot: {
          ...commonSnapshot.snapshot,
          model: this.modelMeta.snapshotCodec.encodeAsAny(this._stateMachine.snapshot())
        }
      };
    } else {
      return {
        ...commonSnapshot,
        mutations: [
          ...(_c = this._initialState.mutations) != null ? _c : [],
          ...this._mutationQueue.getConfirmedMutations().map((entry) => entry.mutation)
        ]
      };
    }
  }
  /**
  * Reset the state to existing snapshot.
  */
  resetToSnapshot(snapshot) {
    var _a, _b, _c, _d, _e;
    assert5(snapshot.genesis);
    assert5(snapshot.objectId === this._id);
    const needsReset = !((_a = this._initialState.meta) == null ? void 0 : _a.clientTag) || !!((_b = this._initialState.meta) == null ? void 0 : _b.feedKey) || ((_c = this._initialState.meta) == null ? void 0 : _c.clientTag) !== ((_d = snapshot.meta) == null ? void 0 : _d.clientTag);
    this._initialState = snapshot;
    this._initialStateMutations = ((_e = this._initialState.mutations) != null ? _e : []).map((mutation) => this._decodeMutation(mutation));
    log2("resetToSnapshot", {
      needsReset,
      snapshot
    }, {
      file: "item.ts",
      line: 330,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    if (needsReset) {
      this._mutationQueue.resetConfirmed();
      if (this.initialized) {
        this._resetState();
      }
    }
  }
};
__decorate([
  logInfo
], Item.prototype, "_debugLabel", void 0);
__decorate([
  logInfo
], Item.prototype, "id", null);
__decorate([
  logInfo
], Item.prototype, "modelType", null);
__decorate([
  logInfo
], Item.prototype, "initialized", null);

// packages/core/echo/echo-db/src/packlets/database/item-demuxer.ts
import assert6 from "@dxos/node-std/assert";
import { Event as Event5 } from "@dxos/async";
import { log as log3 } from "@dxos/log";
var ItemDemuxer = class {
  constructor(_itemManager, _modelFactory) {
    this._itemManager = _itemManager;
    this._modelFactory = _modelFactory;
    this.mutation = new Event5();
  }
  open() {
    this._modelFactory.registered.on(async (model) => {
      for (const item of this._itemManager.getUninitializedEntities()) {
        if (item.modelType === model.meta.type) {
          await this._itemManager.initializeModel(item.id);
        }
      }
    });
    return async (message) => {
      var _a;
      const { batch, meta } = message;
      for (const object of (_a = batch.objects) != null ? _a : []) {
        const { objectId, genesis, mutations } = object;
        assert6(objectId);
        if (genesis) {
          const { modelType } = genesis;
          assert6(modelType);
          const entity = this._itemManager.constructItem({
            itemId: objectId,
            modelType
          });
          setMetadataOnObject(object, meta);
          entity.resetToSnapshot(object);
          assert6(entity.id === objectId);
        } else {
          if (mutations && mutations.length > 0) {
            for (const mutation of mutations) {
              this._itemManager.processMutation(objectId, {
                ...mutation,
                meta
              });
            }
          }
        }
      }
      this.mutation.emit(message);
    };
  }
  createSnapshot() {
    return {
      items: this._itemManager.items.map((item) => item.createSnapshot())
    };
  }
  restoreFromSnapshot(snapshot) {
    var _a;
    const { items = [] } = snapshot;
    log3(`Restoring ${items.length} items from snapshot.`, {}, {
      file: "item-demuxer.ts",
      line: 88,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    for (const item of sortItemsTopologically(items)) {
      assert6(item.objectId);
      assert6((_a = item.genesis) == null ? void 0 : _a.modelType);
      assert6(item.snapshot);
      const obj = this._itemManager.constructItem({
        itemId: item.objectId,
        modelType: item.genesis.modelType
      });
      obj.resetToSnapshot(item);
    }
  }
};
var sortItemsTopologically = (items) => {
  var _a;
  const snapshots = [];
  const seenIds = /* @__PURE__ */ new Set();
  while (snapshots.length !== items.length) {
    const prevLength = snapshots.length;
    for (const item of items) {
      assert6(item.objectId);
      if (!seenIds.has(item.objectId) && (!((_a = item.snapshot) == null ? void 0 : _a.parentId) || seenIds.has(item.snapshot.parentId))) {
        snapshots.push(item);
        seenIds.add(item.objectId);
      }
    }
    if (prevLength === snapshots.length && snapshots.length !== items.length) {
      throw new Error("Cannot topologically sorts items in snapshot: some parents are missing.");
    }
  }
  return snapshots;
};

// packages/core/echo/echo-db/src/packlets/database/item-manager.ts
import assert7 from "@dxos/node-std/assert";
import { failUndefined } from "@dxos/debug";
import { log as log4, logInfo as logInfo2 } from "@dxos/log";
var __decorate2 = function(decorators, target, key, desc) {
  var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function")
    r = Reflect.decorate(decorators, target, key, desc);
  else
    for (var i = decorators.length - 1; i >= 0; i--)
      if (d = decorators[i])
        r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var ItemManager = class {
  /**
  * @param _writeStream Outbound `dxos.echo.IEchoObject` mutation stream.
  */
  constructor(_modelFactory) {
    this._modelFactory = _modelFactory;
    this._entities = /* @__PURE__ */ new Map();
  }
  get entities() {
    return this._entities;
  }
  get items() {
    return Array.from(this._entities.values()).filter((item) => item instanceof Item);
  }
  async destroy() {
    log4("destroying..", {}, {
      file: "item-manager.ts",
      line: 48,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    for (const item of this._entities.values()) {
      await item.destroy();
    }
    this._entities.clear();
  }
  /**
  * Adds new Item to the tracked set. Sets up events and notifies any listeners waiting for this Item to be constructed.
  */
  // TODO(burdon): Parent not used.
  _addItem(item, parent) {
    assert7(!this._entities.has(item.id));
    this._entities.set(item.id, item);
    log4("New Item:", String(item), {
      file: "item-manager.ts",
      line: 62,
      scope: this,
      callSite: (f, a) => f(...a)
    });
  }
  /**
  * Constructs an item with the appropriate model.
  */
  constructItem({ itemId, modelType }) {
    var _a;
    assert7(itemId);
    assert7(modelType);
    if (this.entities.has(itemId)) {
      log4.info("init twice", {}, {
        file: "item-manager.ts",
        line: 72,
        scope: this,
        callSite: (f, a) => f(...a)
      });
      return this.entities.get(itemId);
    }
    const { constructor: modelConstructor } = (_a = this._modelFactory.getModel(modelType)) != null ? _a : failUndefined();
    const item = new Item(this, itemId);
    item._debugLabel = this._debugLabel;
    item.initialize(modelConstructor);
    this._addItem(item);
    return item;
  }
  /**
  * Process a message directed to a specific model.
  * @param itemId Id of the item containing the model.
  * @param message Encoded model message
  */
  processMutation(itemId, mutation) {
    const item = this._entities.get(itemId);
    assert7(item);
    item.processMessage(mutation);
  }
  /**
  * Retrieves a item from the index.
  * @param itemId
  */
  getItem(itemId) {
    const item = this._entities.get(itemId);
    return item;
  }
  getUninitializedEntities() {
    return Array.from(this._entities.values()).filter((item) => !item.initialized);
  }
  /**
  * Recursive method to unlink and remove items from the active set.
  * @param itemId
  */
  deconstructItem(itemId) {
    const item = this._entities.get(itemId);
    assert7(item);
    this._entities.delete(itemId);
  }
  /**
  * Reconstruct an item with a default model when that model becomes registered.
  * New model instance is created and streams are reconnected.
  */
  initializeModel(itemId) {
    const item = this._entities.get(itemId);
    assert7(item);
    const model = this._modelFactory.getModel(item.modelType);
    assert7(model, "Model not registered");
    item.initialize(model.constructor);
  }
};
__decorate2([
  logInfo2
], ItemManager.prototype, "_debugLabel", void 0);

// packages/core/echo/echo-db/src/packlets/database/selection/util.ts
var dedupe = (values) => Array.from(new Set(values));
var coerceToId = (item) => {
  if (typeof item === "string") {
    return item;
  }
  return item.id;
};
var testOneOrMultiple = (expected, value) => {
  if (Array.isArray(expected)) {
    return expected.includes(value);
  } else {
    return expected === value;
  }
};

// packages/core/echo/echo-db/src/packlets/database/selection/queries.ts
var ItemFilterDeleted;
(function(ItemFilterDeleted2) {
  ItemFilterDeleted2[ItemFilterDeleted2[
    /**
    * Do not return deleted items. Default behaviour.
    */
    "HIDE_DELETED"
  ] = 0] = "HIDE_DELETED";
  ItemFilterDeleted2[ItemFilterDeleted2[
    /**
    * Return deleted and regular items.
    */
    "SHOW_DELETED"
  ] = 1] = "SHOW_DELETED";
  ItemFilterDeleted2[ItemFilterDeleted2[
    /**
    * Return only deleted items.
    */
    "SHOW_DELETED_ONLY"
  ] = 2] = "SHOW_DELETED_ONLY";
})(ItemFilterDeleted || (ItemFilterDeleted = {}));
var filterToPredicate = (filter) => {
  if (typeof filter === "function") {
    return filter;
  }
  return itemFilterToPredicate(filter);
};
var itemFilterToPredicate = (filter) => {
  if ("id" in filter) {
    return (item) => item.id === filter.id;
  } else {
    return (item) => // (!filter.type || testOneOrMultiple(filter.type, item.type)) &&
    !filter.parent || item.parent === coerceToId(filter.parent);
  }
};
var createQueryOptionsFilter = ({ deleted = ItemFilterDeleted.HIDE_DELETED }) => (entity) => {
  switch (deleted) {
    case ItemFilterDeleted.HIDE_DELETED:
      return !(entity instanceof Item) || !entity.deleted;
    case ItemFilterDeleted.SHOW_DELETED:
      return true;
    case ItemFilterDeleted.SHOW_DELETED_ONLY:
      return entity instanceof Item && entity.deleted;
  }
};
export {
  Batch,
  DatabaseProxy,
  Item,
  ItemDemuxer,
  ItemFilterDeleted,
  ItemManager,
  ResultSet,
  Schema,
  TYPE_SCHEMA,
  coerceToId,
  createModelMutation,
  createQueryOptionsFilter,
  dedupe,
  encodeModelMutation,
  filterToPredicate,
  genesisMutation,
  itemFilterToPredicate,
  resultSetToStream,
  setMetadataOnObject,
  sortItemsTopologically,
  streamToResultSet,
  tagMutationsInBatch,
  testOneOrMultiple
};
//# sourceMappingURL=index.mjs.map
